import {Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';


import { AccountingSettings, Customer } from '../../../../core/models';
import { TranslationService } from '../../../../core/services';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-accounting-settings',
    templateUrl: './accounting-settings.component.html',
    styleUrls: ['./accounting-settings.component.scss'],
    standalone: false
})
export class AccountingSettingsComponent implements OnInit, OnChanges {

  @Input() settings: AccountingSettings;
  @Output() saveSettings: EventEmitter<Record<string, unknown>> = new EventEmitter();

  customer: Customer;
  form: FormGroup = new FormGroup({
    system: new FormControl('generic', [Validators.required]),
    payment_credit_account: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(20)]),
    payment_debit_account: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(20)]),
    provision_account_no: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(20)]),
    account_no: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(20)]),
    intermediate_account: new FormControl('', [Validators.minLength(3), Validators.maxLength(20)]),
    contra_debit_account_outgoing: new FormControl(null, [Validators.minLength(3), Validators.maxLength(20)]),
    contra_credit_account_outgoing: new FormControl(null, [Validators.minLength(3), Validators.maxLength(20)]),
    contra_debit_account_incoming: new FormControl(null, [Validators.minLength(3), Validators.maxLength(20)]),
    contra_credit_account_incoming: new FormControl(null, [Validators.minLength(3), Validators.maxLength(20)]),
    dimensions: new FormArray([]),
    dimension_mandatory: new FormControl(false),
    export_posting_attachments: new FormControl(false),
    export_postings: new FormControl(false),
    // MultiRegnskab
    multi_regnskab: new FormGroup({
      portfolio_customer_id: new FormControl<string>(null, [Validators.required]),
      authorization: new FormControl<string>(null, [Validators.required]),
      url: new FormControl<string>(null, [Validators.required]),
    }),
    // e-conomic
    token: new FormControl<string>(null),
    journal_no: new FormControl<string>(null),
    // Uniconta specific fields
    journal: new FormControl<string>(null, [Validators.required]),
    company_id: new FormControl<string>(null, [Validators.required]),
    username: new FormControl<string>(null, [Validators.required]),
    password: new FormControl<string>(null, [Validators.required]),
    uniconta_fields: new FormArray([]),
    // Opus specific settings
    posting_date_as_current_date: new FormControl<boolean>(false),
    comp_code: new FormControl<string>(null, [Validators.required]),
    municipality_no: new FormControl<string>(null, [Validators.required]),
    prod_env: new FormControl<string>(null),
    system_id: new FormControl<string>(null),
    data_provider_id: new FormControl<string>(null),
    // Generic w/Navision CSV file format
    csv_options: new FormControl<any>(null),
    csv_skip_header: new FormControl<boolean>(false),
    // General
    batch_size: new FormControl<number>(25),
    project_provision_account: new FormControl<boolean>(false),
    same_year_reverse_provision_account: new FormControl<string>(null),
    special_reverse_provision_account: new FormControl<string>(null),
    account_type_prefix: new FormControl<string>(null),
    allow_reset: new FormControl<boolean>(false),
    flip_account_contra: new FormControl<boolean>(false),
    flip_sign: new FormControl<boolean>(false),
    'file-format': new FormControl<'PostingsCsv' | 'NavisionPostingsCsv' | 'PostingsXlsx' | 'OpusPostingsXml'>('PostingsCsv', [Validators.required]),
    'file-columns': new FormArray([]),
    payment_type: new FormControl<any>(null),
    encoding: new FormControl<string>(null),
    decimal_places: new FormControl<number>(2, [Validators.required, Validators.min(0), Validators.max(10)]),
    decimal_separator: new FormControl<string>('.', [Validators.required, Validators.minLength(1), Validators.maxLength(1)]),
    use_local_currency: new FormControl<boolean>(false),
    sftp: new FormGroup({
      host: new FormControl<string>(null),
      user: new FormControl<string>(null),
      password_secret: new FormControl<string>(null),
      key: new FormControl<string>(null)
    }),
    settle_exchange_rate_diff: new FormControl<boolean>(false),
    exchange_rate_diff_credit_account: new FormControl<string>(null, [Validators.minLength(3), Validators.maxLength(20)]),
    exchange_rate_diff_debit_account: new FormControl<string>(null, [Validators.minLength(3), Validators.maxLength(20)]),
  });
  value: AccountingSettings = {
    system: 'generic',
    username: null,
    password: null,
    company_id: null,
    journal: null,
    payment_credit_account: '',
    payment_debit_account: '',
    provision_account_no: '',
    account_no: '',
    intermediate_account: '',
    contra_debit_account_outgoing: null,
    contra_credit_account_outgoing: null,
    contra_debit_account_incoming: null,
    contra_credit_account_incoming: null,
    dimension_mandatory: false,
    export_posting_attachments: false
  };
  economic_posting_text: FormControl = new FormControl('', [Validators.required]);
  canExportAttachment: AccountingSettings['system'][] = ['economic', 'dynamic', 'generic']
  sftpSecretType: FormControl<'key' | 'password_secret'> = new FormControl('key');
  passwordSecretPresent: boolean = false;

  /**
   * Controls that only apply to specific økonomisystemer. Controls left out of this list apply to
   * every system. The controls always stay on the form — only their enabled state changes — so a
   * control keeps its value when the system is switched away from it. Disabled controls are left
   * out of `form.value`, and thereby out of the saved settings.
   */
  private readonly systemSpecificControls: { name: string, systems: AccountingSettings['system'][] }[] = [
    { name: 'multi_regnskab', systems: ['multi_regnskab'] },
    { name: 'company_id', systems: ['uniconta'] },
    { name: 'username', systems: ['uniconta'] },
    { name: 'password', systems: ['uniconta'] },
    { name: 'journal', systems: ['uniconta'] },
    { name: 'uniconta_fields', systems: ['uniconta'] },
    { name: 'comp_code', systems: ['opus'] },
    { name: 'municipality_no', systems: ['opus'] },
    { name: 'posting_date_as_current_date', systems: ['opus'] },
    { name: 'prod_env', systems: ['opus'] },
    { name: 'system_id', systems: ['opus'] },
    { name: 'data_provider_id', systems: ['opus'] },
    { name: 'csv_options', systems: ['generic'] },
    { name: 'csv_skip_header', systems: ['generic'] },
    { name: 'export_posting_attachments', systems: this.canExportAttachment },
  ];

  constructor(private route: ActivatedRoute,
              private translationsServices: TranslationService,
              private translate: TranslateService,
              private destroyRef: DestroyRef) {
    this.route.data.subscribe(data => {
      this.customer = data['customer'];
      const lang  = this.customer.settings.board?.languages?.[0] || this.customer.settings.board?.language || 'da';
      this.translationsServices.getTranslations(this.customer.subdomain, lang).subscribe(translations => {
        this.translate.use(lang);
        this.translate.setTranslation(lang, translations, true);
        this.translate.get('accounting_connector.economic.text').subscribe(value => {
          this.economic_posting_text.patchValue(value)
        });
      });
    });
    this.form.get('export_postings').valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      const sftpForm = this.form.get('sftp') as FormGroup;
      let validators = [];
      if (value) {
        validators.push(Validators.required);
      }
      sftpForm.get('host').setValidators(validators);
      sftpForm.get('user').setValidators(validators);
    });
  }

  ngOnInit(): void {
    this.form.disable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['settings']) {
      this.settings = changes['settings'].currentValue;
      if (this.settings) {
        this.value = { ...this.settings };
      }
      if (!this.value.system) {
        this.value.system = 'generic';
      }
      this.passwordSecretPresent = !!this.value.sftp?.password_secret_present;
      this.sftpSecretType.setValue(this.passwordSecretPresent ? 'password_secret' : 'key');
      this.handleSystemChange({value: this.value.system});
      this.dimensions.clear();
      if (this.value.dimensions) {
        this.value.dimensions.forEach(d => this.doAddDimension(d));
      }
      this.uniconta_fields.clear();
      if (this.value.uniconta_fields) {
        this.value.uniconta_fields.forEach(f => this.doAddUnicontaField(f));
      }
      this.form.patchValue(this.value);
    }
  }

  handleSystemChange(event: { value: AccountingSettings['system'] }): void {
    const system = event.value;
    // ngOnChanges also calls this while the panel is in read mode. Enabling a control there would
    // make the form as a whole enabled and flip the template into edit mode.
    const editing = this.form.enabled;

    this.systemSpecificControls.forEach(({ name, systems }) => {
      const control: AbstractControl = this.form.get(name);
      if (editing && systems.includes(system)) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });

    // Uniconta & e-conomic fields
    if (!['uniconta', 'economic', 'opus', 'multi_regnskab'].includes(system)) {
      this.form.get('export_postings').setValue(false);
    }

    this.form.updateValueAndValidity();
  }

  doEdit(): void {
    this.form.enable();
    // enable() enables every control, also the ones that do not apply to the selected system.
    this.handleSystemChange({ value: this.form.get('system').value as AccountingSettings['system'] });
  }

  doCancel(): void {
    this.value = { ...this.settings };
    if (!this.value.system) {
      this.value.system = 'generic';
    }
    this.dimensions.clear();
    if (this.value.dimensions) {
      this.value.dimensions.forEach(d => this.doAddDimension(d));
    }
    this.uniconta_fields.clear();
    if (this.value.uniconta_fields) {
      this.value.uniconta_fields.forEach(f => this.doAddUnicontaField(f));
    }
    this.form.reset(this.value);
    this.form.disable()
  }

  doSave(): void {
    this.translationsServices.setTranslation(this.customer.subdomain, 'da', 'accounting_connector.economic.text',
                                             this.economic_posting_text.value).subscribe(() => {});

    const value = { ...this.form.value };

    // Handle export_postings
    const sftp = value.sftp;
    if (!value.export_postings) {
      sftp.key = '';
      sftp.password_secret = '';
    } else if (this.sftpSecretType.value !== 'password_secret') {
      sftp.password_secret = '';
    } else if (this.passwordSecretPresent && !sftp.password_secret) {
      delete sftp.password_secret;
    }

    this.saveSettings.emit(value);
    this.form.disable();
  }

  get uniconta_fields(): FormArray {
    return this.form.get('uniconta_fields') as FormArray;
  }

  doAddUnicontaField(field: { name: string, value: string } = { name: '', value: '' }): void {
    const group: FormGroup = new FormGroup({
      name: new FormControl(field.name, [Validators.required]),
      value: new FormControl(field.value, [Validators.required]),
    });
    // An enabled group in a disabled FormArray enables the array, and thereby the whole form.
    if (this.uniconta_fields.disabled) {
      group.disable({ emitEvent: false });
    }
    this.uniconta_fields.push(group);
  }

  doRemoveUnicontaField(index: number): void {
    this.uniconta_fields.removeAt(index);
  }


  doAddDimension(dimension: { name: string, code: string } = { name: '', code: '' }): void {
    const group: FormGroup = new FormGroup({
      name: new FormControl(dimension.name, [Validators.required]),
      code: new FormControl(dimension.code, [Validators.required]),
    });
    // An enabled group in a disabled FormArray enables the array, and thereby the whole form.
    if (this.dimensions.disabled) {
      group.disable({ emitEvent: false });
    }
    this.dimensions.push(group);
  }

  doRemoveDimension(index: number): void {
    this.dimensions.removeAt(index);
  }

  get dimensions(): FormArray {
    return this.form.get('dimensions') as FormArray;
  }

}
